<script setup lang="ts">
    import { computed, ref, toRef } from 'vue'

    import { insertIntoTextArea1 } from '@common/Common'

    import type {
        AlternativesViewReadModel,
        AttributeReadModel
    } from '@Evaluation/Estimations/Domain/readmodels'

    import
        {
            PanelAlternativesManagerSaveEventPayload
        } from '@Evaluation/Estimations/Components/PanelAlternativesManager'
    import type {
        PanelAlternativesManagerViewModel
    } from '@Evaluation/Estimations/Components/PanelAlternativesManager'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: PanelAlternativesManagerViewModel
    }>()

    const emit = defineEmits<{
        (e: 'save', payload: PanelAlternativesManagerSaveEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- refs ----------------

    const ta = ref<HTMLTextAreaElement | null>(null)

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const alternativesLabel = computed(() => vm.value.alternativesLabel)
    const attributes = computed<AttributeReadModel[]>(() => vm.value.attributes)
    const views = computed<AlternativesViewReadModel[]>(() => vm.value.views)
    const emailAttributes = computed<AttributeReadModel[]>(() => vm.value.emailAttributes)

    const descriptionTemplate = computed<string>({
        get: () => vm.value.descriptionTemplate,
        set: val => { vm.value.descriptionTemplate = val }
    })

    const viewDefId = computed<string | null>({
        get: () => vm.value.viewDefId,
        set: val => { vm.value.viewDefId = val }
    })

    const attributeId = computed<number | null>({
        get: () => vm.value.attributeId,
        set: val => { vm.value.attributeId = val }
    })

    // ---------------- actions ----------------

    function insert(txtToAdd: string)
    {
        if (!ta.value)
            return

        insertIntoTextArea1(ta.value, txtToAdd)
        ta.value.dispatchEvent(new Event('input'))
    }

    function save()
    {
        const payload = new PanelAlternativesManagerSaveEventPayload(
            vm.value.surveyId,
            descriptionTemplate.value,
            viewDefId.value,
            attributeId.value
        )

        emit('save', payload)
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content" style="height: calc(100vh - 50px);">
                    <div class="modal-header">
                        <h5 class="modal-title">Manage <span class="text-lowercase">{{alternativesLabel}}</span></h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label>List:</label>
                            <select v-model="viewDefId" class="form-control">
                                <option :value="null">All</option>
                                <option v-for="v in views" :value="v.Id">{{v.Name}}</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label>Filter by attribute:</label>
                            <select v-model="attributeId" class="form-control">
                                <option :value="null">No selection</option>
                                <option v-for="attr in emailAttributes" :value="attr.AttributeId">{{attr.Name}}</option>
                            </select>
                        </div>

                        <div>
                            <div class="d-flex justify-content-end">
                                <div class="dropdown">
                                    <button class="btn btn-sm btn-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                    <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                        <a class="dropdown-item" @click="insert('{Description}')">Description</a>
                                        <a class="dropdown-item" @click="insert('{Status}')">Status</a>

                                        <a class="dropdown-item" v-for="attr in attributes" :key="attr.AttributeId" @click="insert(`{${attr.Name}}`)">
                                            {{attr.Name}}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group">
                                <textarea ref="ta" rows="5" v-model.trim="descriptionTemplate" class="form-control" />
                                <small class="form-text text-muted"></small>
                            </div>

                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="save" type="button" class="btn btn-primary">Save &amp; Close</button>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>